import React, { useState, useEffect, useRef } from 'react';
import { 
  Table, Input, Button, Space, Switch, Modal, 
  Tag, notification, Card, Tooltip, Dropdown, Menu,
  Badge,
  Select
} from 'antd';
import { 
  PlusOutlined, EyeOutlined, EditOutlined, DeleteOutlined, 
  FilterOutlined, ReloadOutlined, MoreOutlined, DownloadOutlined, 
  SearchOutlined, ExclamationCircleOutlined, UserSwitchOutlined
} from '@ant-design/icons';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import userService from '../../../services/userService';
import userImpersonationService from '../../../services/userImpersonationService';
import config from '../../../config';
import { AddModal, EditModal, ViewModal } from './modals';
import ImpersonationBanner from './ImpersonationBanner';
import userRolePermissionService from '../../../services/userRolePermissionService';
import usePermission from '../../../hooks/usePermission';
const { Option } = Select;

const { Search } = Input;
const { confirm } = Modal;

const UsersList = ({status = null}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const { hasPermission } = usePermission();

  const [filters, setFilters] = useState({
    status: null,
    user_type: null
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [sortInfo, setSortInfo] = useState({
    columnKey: 'id',
    order: 'descend'
  });

  //if searchText is larger than 2 characters filter the users

  useEffect(() => {
    if (searchText.length > 2 && users) {
      const filteredUsers = users.filter(user => {
        return (
          user.name?.toLowerCase().includes(searchText.toLowerCase()) ||
          user.email?.toLowerCase().includes(searchText.toLowerCase()) ||
          user.company_name?.toLowerCase().includes(searchText.toLowerCase())
        );
      }
      );
      setUsers(filteredUsers);
    } else {
      fetchUsers();
    }
  }, [searchText,status]);

  
  // Modal states
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  
  const searchInputRef = useRef(null);

  useEffect(() => {
    fetchUsers();
  }, [filters, pagination.current, pagination.pageSize, sortInfo]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.current,
        limit: pagination.pageSize,
        search: searchText,
        status: (status==0 ? 100:null) || filters.status,
        user_type: filters.user_type,
        sort_field: sortInfo.columnKey,
        sort_order: sortInfo.order === 'ascend' ? 'asc' : 'desc'
      };
      
      const response = await userService.getAllCustomers(params);
      setUsers(response.data || []);
      setPagination({
        ...pagination,
        total: response.total || 0
      });
    } catch (error) {
      notification.error({
        message: 'Error fetching users',
        description: error.message || 'Failed to load users'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (pagination, filters, sorter) => {
    setPagination(pagination);
    
    // Update sortInfo if sorter has column key
    if (sorter && sorter.columnKey) {
      setSortInfo({
        columnKey: sorter.columnKey,
        order: sorter.order
      });
    } else {
      setSortInfo({ columnKey: 'id', order: 'descend' });
    }
  };

  const handleSearch = (value) => {
    setSearchText(value);
    setPagination({ ...pagination, current: 1 });
    fetchUsers();
  };

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value });
    setPagination({ ...pagination, current: 1 });
  };

  const handleResetFilters = () => {
    setFilters({ status: null, user_type: null });
    setSearchText('');
    setSortInfo({ columnKey: 'id', order: 'descend' });
    setPagination({ ...pagination, current: 1 });
    if (searchInputRef.current) {
      searchInputRef.current.input.value = '';
    }
    fetchUsers();
  };

  const showDeleteConfirm = (userId) => {
    confirm({
      title: 'Are you sure you want to delete this user?',
      icon: <ExclamationCircleOutlined />,
      content: 'This action cannot be undone.',
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk() {
        handleDelete(userId);
      }
    });
  };

  const handleDelete = async (userId) => {
    try {
      await userService.deleteUser(userId);
      notification.success({
        message: 'User deleted successfully'
      });
      fetchUsers();
    } catch (error) {
      notification.error({
        message: 'Error deleting user',
        description: error.message || 'Failed to delete user'
      });
    }
  };

  const handleStatusChange = async (checked, userId) => {
    try {
      await userService.updateUserStatus(userId, checked ? 1 : 0);
      notification.success({
        message: `User ${checked ? 'activated' : 'deactivated'} successfully`
      });
      
      // Update the user status in the current list without refetching
      setUsers(prevUsers => 
        prevUsers?.map(user => 
          user.id === userId 
            ? { ...user, status: checked ? 1 : 0 } 
            : user
        )
      );
    } catch (error) {
      notification.error({
        message: 'Error updating user status',
        description: error.message || 'Failed to update user status'
      });
    }
  };

  const handleExport = async () => {
    try {
      await userService.exportUsers();
      notification.success({
        message: 'Export started',
        description: 'Users export has been initiated. You will be notified when it\'s ready for download.'
      });
    } catch (error) {
      notification.error({
        message: 'Error exporting users',
        description: error.message || 'Failed to export users'
      });
    }
  };

// This code should replace the existing handleImpersonateUser function in UsersList.js

const handleImpersonateUser = async (userId) => {
  try {
    setLoading(true);
    
    // Make sure any existing impersonation is cleared first
    if (userImpersonationService.isImpersonating()) {
      userImpersonationService.stopImpersonation();
    }
    
    const result = await userImpersonationService.impersonateUser(userId);
    console.log('Impersonation result:', result);
    
    if (result.success) {
      // Successfully started impersonation
      notification.success({
        message: 'Impersonation Started',
        description: `You are now viewing the application as ${result.user.name || result.user.email}`,
        duration: 3
      });

      // Important: Load the impersonated user's permissions
      if (result.user && result.user.id) {
        try {
          // Use an imported service to load permissions for the impersonated user
          await userRolePermissionService.loadUserPermissions(result.user.id);
        } catch (error) {
          console.error('Error loading impersonated user permissions:', error);
        }
      }
      
      // Manually dispatch events to update permission-dependent components
      window.dispatchEvent(new Event('permissionsChanged'));
      
      // Redirect to the dashboard with a slight delay to ensure everything is updated
      setTimeout(() => {
        // For regular users, go to regular dashboard, for admins, stay in admin area
        const dashboardPath = result.user.role === 'admin' ? '/admin/dashboard' : '/';
        window.location.href = dashboardPath;
      }, 1000);
    } else {
      // Handle impersonation failure
      notification.error({
        message: 'Impersonation Failed',
        description: result.message || 'Unable to impersonate user',
        duration: 4
      });
    }
  } catch (error) {
    console.error('Error during impersonation:', error);
    notification.error({
      message: 'Impersonation Error',
      description: 'An unexpected error occurred while trying to impersonate user',
      duration: 4
    });
  } finally {
    setLoading(false);
  }
};

  // Modal handlers
  const showAddModal = () => {
    setAddModalVisible(true);
  };

  const showEditModal = (userId) => {
    setSelectedUserId(userId);
    setEditModalVisible(true);
  };

  const showViewModal = (userId) => {
    setSelectedUserId(userId);
    setViewModalVisible(true);
  };

  const handleModalSuccess = () => {
    fetchUsers();
    setAddModalVisible(false);
    setEditModalVisible(false);
  };

  const closeModals = () => {
    setAddModalVisible(false);
    setEditModalVisible(false);
    setViewModalVisible(false);
    setSelectedUserId(null);
  };

  const getUserTypeTag = (userRoles) => {
  
   console.log('User Roles:', userRoles);
  //if user roles contans only admin or superadmin return admin
  if (userRoles.length === 1 && (userRoles[0].name === 'admin' || userRoles[0].name === 'superadmin')) {
    return (
      <Tag color="blue" className="text-xs">
        Admin
      </Tag>
    );
  }
  //if user roles contains only customer return customer
  if (userRoles.length === 1 && userRoles[0].name === 'customer') {
    return (
      <Tag color="green" className="text-xs">
        Customer
      </Tag>
    );
  }
  //if user roles contains agency return agency
  if (userRoles.length > 0 && userRoles[0].name === 'agency') {
    return (
      <Tag color="orange" className="text-xs">
        Agency
      </Tag>
    );
  }

  //else return join name of roles and return as tag
  const userTypeNames = userRoles.map(role => role.name).join(', ');
  return (
    <Tag color="purple" className="text-xs">
      {userRoles.length>0 ? userTypeNames : 'No roles'}
    </Tag>
  );
    
  };

  // Check if the current user is an admin
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = currentUser.role === 'admin' || currentUser.isAdmin;

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      sorter: true,
      width: 80,
      fixed: 'left'
    },
    {
      title: 'Name',
      key: 'name',
      render: (text, record) => (
        <div>
          <div className="font-medium">{record.name || `${record.first_name || ''} ${record.last_name || ''}`}</div>
          <div className="text-xs text-gray-500">{record.email}</div>
          {record.phone && (
            <div className="text-xs text-gray-500">{record.phone}</div>
          )}
        </div>
      ),
      sorter: true,
      ellipsis: true
    },
    {
      title: 'Company',
      key: 'company',
      render: (text, record) => (
        <div>
          {record.company_name ? (
            <div className="font-medium">{record.company_name}</div>
          ) : (
            <div className="text-gray-400">No company</div>
          )}
          {record.company_registration && (
            <div className="text-xs text-gray-500">Reg: {record.company_registration}</div>
          )}
        </div>
      ),
      sorter: true,
      ellipsis: true
    },
    {
      title: 'Address',
      key: 'address',
      render: (text, record) => (
        <div>
          {record.apartment_number && <span>{record.apartment_number}, </span>}
          {record.address && <span>{record.address}, </span>}
          {record.city && <span>{record.city}, </span>}
          {record.post_code && <span>{record.post_code}</span>}
          {!record.apartment_number && !record.address && !record.city && !record.post_code && (
            <span className="text-gray-400">No address</span>
          )}
        </div>
      ),
      sorter: true,
      ellipsis: true
    },
    {
      title: 'Roles',
      dataIndex: 'roles',
      key: 'roles',
      render: (roles) => getUserTypeTag(roles),
      filters: [
        { text: 'Agency', value: '1' },
        { text: 'Customer', value: '0' }
      ],
      filtered: filters.user_type !== null,
      filterMultiple: false,
      width: 120
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status, record) => (
        <Switch 
          checked={status === 1 || status === '1'} 
          disabled={!hasPermission('edit_users')}
          onChange={(checked) => handleStatusChange(checked, record.id)} 
        />
      ),
     
      filtered: filters.status !== null,
      filterMultiple: false,
      width: 100
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => new Date(date).toLocaleDateString(),
      sorter: true,
      width: 120
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="small">
          {hasPermission('view_users') &&<Tooltip title="View Details">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => showViewModal(record.id)}
              className="text-blue-500 hover:text-blue-700"
            />
          </Tooltip>}
          {hasPermission('edit_users') && <Tooltip title="Edit User">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => showEditModal(record.id)}
              className="text-yellow-500 hover:text-yellow-700"
            />
          </Tooltip>}
          {hasPermission('delete_users') &&<Tooltip title="Delete User">
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => showDeleteConfirm(record.id)}
              className="text-red-500 hover:text-red-700"
            />
          </Tooltip>}
          {/* Only show impersonation button for admins */}
         {hasPermission('impersonate_users')  && (
            <Tooltip title="Impersonate User">
              <Button
                type="text"
                icon={<UserSwitchOutlined />}
                onClick={() => handleImpersonateUser(record.id)}
                className="text-green-500 hover:text-green-700"
              />
            </Tooltip>
          )}
        </Space>
      ),
      width: 160,
      fixed: 'right'
    }
  ];

  const actionsMenu = (
    <Menu>
      <Menu.Item key="export" onClick={handleExport} icon={<DownloadOutlined />}>
        Export Users
      </Menu.Item>
    </Menu>
  );
  // Custom pagination configuration
  const getPaginationConfig = () => {
    const totalRecords = pagination.total || users.length;
    
    return {
      ...pagination,
      showSizeChanger: true,
      showQuickJumper: true,
      showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
      pageSizeOptions: ['5', '10', '50', String(totalRecords)],
      showLessItems: true,
      itemRender: (current, type, originalElement) => {
        if (type === 'page') {
          return <a>{current}</a>;
        }
        return originalElement;
      },
      // Custom page size option labels
      selectComponentClass: (props) => (
        <Select {...props}>
          <Option value="5">5 / page</Option>
          <Option value="10">10 / page</Option>
          <Option value="50">50 / page</Option>
          <Option value={String(totalRecords)}>All</Option>
        </Select>
      )
    };
  };
  return (
    <div className="p-6">
      {/* Display impersonation banner if currently impersonating */}
       
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Users Management</h1>
        <Space>
        {hasPermission('create_users') && <Button
            type="primary"
            onClick={showAddModal}
            icon={<PlusOutlined />}
            className="bg-golden-navy"
          >
            Add New User
          </Button>}
          <Dropdown overlay={actionsMenu}>
            <Button icon={<MoreOutlined />}>Actions</Button>
          </Dropdown>
        </Space>
      </div>

      <Card className="shadow-md mb-6">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
          <div className="flex flex-wrap items-center gap-4">
            <Search
              placeholder="Search users..."
              allowClear
              onSearch={handleSearch}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 250 }}
              ref={searchInputRef}
            />
            
            <Button 
              icon={<ReloadOutlined />} 
              onClick={handleResetFilters}
              className="flex items-center"
            >
              Reset
            </Button>
          </div>

          <div className="flex gap-2">
            <Button
              icon={<DownloadOutlined />}
              onClick={handleExport}
              className="flex items-center"
            >
              Export
            </Button>
            <Button
              type="primary"
              onClick={fetchUsers}
              icon={<ReloadOutlined />}
              className="bg-golden-navy"
            >
              Refresh
            </Button>
          </div>
        </div>

        {users && users.length > 0 && (
          <Table 
            columns={columns} 
            dataSource={users} 
            rowKey="id"
            pagination={getPaginationConfig()}
            loading={loading}
            onChange={handleTableChange}
            scroll={{ x: 1200 }}
            size="middle"
          />
        )}
      </Card>

      {/* Modals */}
      <AddModal 
        visible={addModalVisible}
        onCancel={closeModals}
        onSuccess={handleModalSuccess}
      />

      <EditModal 
        visible={editModalVisible}
        userId={selectedUserId}
        onCancel={closeModals}
        onSuccess={handleModalSuccess}
      />

      <ViewModal 
        visible={viewModalVisible}
        userId={selectedUserId}
        onCancel={closeModals}
      />
    </div>
  );
};

export default UsersList;